import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";

const useTasks = () => {
  const authToken = sessionStorage.getItem("authToken");

  const [state, setState] = useState({
    tasks: [],
    loading: false,
    error: null,
    totalPages: 1,
    totalElements: 0,
  });

  const fetchTasks = useCallback(async (page = 0, pageSize = 10, statusFilter = "") => {
    if (!authToken) return;

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      
      let url = "/olitask-service/api/v1/tasks";
      const params = new URLSearchParams({
        page,
        size: pageSize,
      });
      
      if (statusFilter) {
        url = "/olitask-service/api/v1/tasks/filter";
        params.append("status", statusFilter);
      }

      const response = await apiClient.get(`${url}?${params.toString()}`);

      console.log(response.data.content);

      let content = [];
      let totalPages = 1;
      let totalElements = 0;

      if (Array.isArray(response.data)) {
        content = response.data;
        totalElements = response.data.length;
      } else if (response.data?.content) {
        content = response.data.content || [];
        totalPages = response.data.totalPages || 1;
        totalElements = response.data.totalElements || 0;
      } else if (response.data?.data) {
        content = Array.isArray(response.data.data) ? response.data.data : [];
        totalElements = content.length;
      } else {
        content = [];
      }

      const formattedTasks = content.map(formatTaskData);

      setState({
        tasks: formattedTasks,
        loading: false,
        error: null,
        totalPages,
        totalElements,
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load tasks.";
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [authToken]);

  const getTaskById = useCallback(async (taskId) => {
    if (!taskId) throw new Error("Task ID is required");
    
    setState((prev) => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await apiClient.get(`/olitask-service/api/v1/tasks/${taskId}`);
      console.log("Task details", response.data);
      const task = formatTaskData(response.data);
      setState((prev) => ({ ...prev, loading: false }));
      return task;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const updateTaskStatus = useCallback(async (taskId, status, page = 0, pageSize = 10, statusFilter = "") => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/status`, { status });
      await fetchTasks(page, pageSize, statusFilter);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);

  const deactivateTask = useCallback(async (taskId, page = 0, pageSize = 10, statusFilter = "") => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/deactivate`);
      await fetchTasks(page, pageSize, statusFilter);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);

  const deleteTask = useCallback(async (taskId, page = 0, pageSize = 10, statusFilter = "") => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.delete(`/olitask-service/api/v1/tasks/${taskId}?type=SOFT`);
      await fetchTasks(page, pageSize, statusFilter);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);



  const { tasks, loading, error, totalPages, totalElements } = state;

  const getAllCounterOffers = useCallback(() => {
    return tasks.flatMap((task) =>
      (task.counterOffers || []).map((co) => ({
        ...co,
        taskId: task.id,
        taskTitle: task.title,
        taskStatus: task.status,
        requester: task.requester,
      }))
    );
  }, [tasks]);

  return {
    tasks,
    loading,
    error,
    totalPages,
    totalElements,
    fetchTasks,
    getTaskById,
    updateTaskStatus,
    deactivateTask,
    deleteTask,
    totalTasks: totalElements,
    completedTasks: tasks.filter((t) => t.status === "COMPLETED").length,
    activeTasks: tasks.filter((t) => t.status === "ACTIVE").length,
    pendingTasks: tasks.filter((t) => t.status === "PENDING").length,
    getAllCounterOffers
  };
};

function formatTaskData(task = {}) {
  return {
    id: task.id || "None",
    title: task.title || "Untitled",
    description: task.description || "No description",
    status: task.status || "UNKNOWN",
    priority: task.priority || "NORMAL",
    assignedTo: task.assignedTo || null,
    requester: task.requester || null,
    helper: task.helper || null,
    taskCharges: task.taskCharges || {},
    createdAt: task.createdAt || null,
    updatedAt: task.updatedAt || null,
    photos: task.photos || [],
    location: task.location || {},
    counterOffers: (task.counterOffers || []).map(formatCounterOffer),
  };
}

function formatCounterOffer(co = {}) {
  return {
    id: co.id,

    // helper info
    helper: co.helper,

    // original offer
    amount: co.amount,
    durationMinutes: co.durationMinutes,
    reason: co.reason,
    submittedAt: co.submittedAt,

    // status flow
    status: co.status,
    responseStatus: co.responseStatus,

    // response data
    responseAmount: co.responseAmount,
    responseDurationMinutes: co.responseDurationMinutes,
    responseReason: co.responseReason,
    responseAt: co.responseAt,

    // engagement
    isStillInterested: co.isStillInterested,
  };
}

export default useTasks;